import React from 'react';
import { PropTypes } from 'prop-types';
import { Badge, Divider, Skeleton } from '@mantine/core';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import NumberFormat from 'react-number-format';
import { formatUtcDate } from '../../../helpers/format';
import AppCard from '../../common/AppCard';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import { useMediaQueryIndex } from '../../../helpers/hooks';
import { getResponsiveStyle as rs } from '../../../helpers/styles';
import { PAYMENT_PROVIDER_LINE_ITEM_TYPES } from '../../../config/constants';
import OrderListItemProductSummary from './OrderListItemProductSummary';
import { translateStoreOrderData } from '../../../helpers/storeHelper';
import WsAccountOrderSummaryProductsGrouped from '../ecommerce/webstore/sections/accountContent/WsAccountOrderSummaryProductsGrouped';

const OrderListItem = ({
  failedPaymentPlan,
  outstandingInvoice,
  processingPaymentPlan,
  regCart
}) => {
  const mqIndex = useMediaQueryIndex();
  const {
    ecomOrderProducts: productData,
    productDataGroupedByPackage: groupedProductData
  } = translateStoreOrderData(regCart?.ecomOrder);
  const isPendingPayment = regCart?.regCartProducts.some(
    (p) => p.regFormSubmission.status === 'PENDING_PAYMENT'
  );
  const hasBeenRefunded = regCart?.cart.lineItems.some(
    (i) => i.refundedAmountInCents > 0
  );

  const baseUrl = regCart?.regAssociation
    ? `/association/${regCart.regAssociation.pkRegAssociation}`
    : null;

  return regCart ? (
    <AppCard
      style={{
        padding: 0,
        minWidth: rs([null, null, 600], mqIndex),
        borderRadius: rs([0, 0, 4], mqIndex)
      }}
    >
      <AppStack style={{ gap: 0 }}>
        <AppFlexbox
          style={{
            backgroundColor: '#000',
            color: '#FFF',
            opacity: 0.8,
            padding: '15px 30px',
            justifyContent: 'space-between',
            alignItems: 'start',
            gap: 10,
            flexDirection: rs(['column-reverse', 'row'], mqIndex)
          }}
        >
          <AppFlexbox
            style={{
              gap: rs([10, 40], mqIndex),
              flex: rs([1, 'unset'], mqIndex),
              alignSelf: rs(['stretch', 'unset'], mqIndex),
              justifyContent: 'space-between',
              flexWrap: 'wrap'
            }}
          >
            <AppStack style={{ gap: 0 }}>
              <AppText>ORDER PLACED</AppText>
              <AppText style={{ fontSize: 18 }} weight={700}>
                {regCart?.cart.purchaseDate
                  ? dayjs(formatUtcDate(regCart?.cart.purchaseDate)).format(
                      'MMM D, YYYY'
                    )
                  : 'N/A'}
              </AppText>
            </AppStack>
            <AppStack
              style={{ gap: 0, alignItems: rs(['end', 'start'], mqIndex) }}
            >
              <AppText>TOTAL</AppText>
              <AppFlexbox style={{ gap: 10 }}>
                <AppText style={{ fontSize: 18 }} weight={700}>
                  <NumberFormat
                    decimalScale={2}
                    displayType="text"
                    fixedDecimalScale
                    prefix="$"
                    thousandSeparator
                    value={regCart.cart.totalInCents / 100}
                  />
                </AppText>
                <AppText style={{ fontSize: 18 }} weight={700}>
                  {regCart.cart.currency?.toUpperCase()}
                </AppText>
              </AppFlexbox>
            </AppStack>
            <AppStack style={{ gap: 0 }}>
              <AppText>STATUS</AppText>
              <AppText style={{ fontSize: 18 }} weight={700}>
                {isPendingPayment
                  ? 'PENDING'
                  : hasBeenRefunded
                  ? 'REFUNDED'
                  : 'COMPLETED'}
              </AppText>
            </AppStack>

            {failedPaymentPlan && (
              <AppStack style={{ gap: 5, alignItems: 'start' }}>
                <Badge color="red">PAYMENT FAILED</Badge>
                <AppText
                  component={Link}
                  style={{
                    fontSize: 14,
                    textDecoration: 'none',
                    color: '#FFF'
                  }}
                  to={`/payments/plans/${failedPaymentPlan.pkRegCart}/${failedPaymentPlan.pkRegCartPayment}`}
                  weight={500}
                >
                  Update card details
                </AppText>
              </AppStack>
            )}

            {processingPaymentPlan && (
              <AppStack style={{ gap: 5, alignItems: 'start' }}>
                <Badge color="orange">PAYMENT PROCESSING</Badge>
                <AppText
                  component={Link}
                  style={{
                    fontSize: 14,
                    textDecoration: 'none',
                    color: '#FFF'
                  }}
                  to={`/payments/plans/${processingPaymentPlan.pkRegCart}/${processingPaymentPlan.pkRegCartPayment}`}
                  weight={500}
                >
                  View payment plan
                </AppText>
              </AppStack>
            )}

            {outstandingInvoice && (
              <AppStack style={{ gap: 5, alignItems: 'start' }}>
                <Badge color="orange">PAYMENT DUE</Badge>
                {outstandingInvoice.isPayable && (
                  <AppText
                    component={Link}
                    style={{
                      fontSize: 14,
                      textDecoration: 'none',
                      color: '#FFF'
                    }}
                    to={`/checkout/invoices/${outstandingInvoice.stripeInvoiceId}`}
                    weight={500}
                  >
                    Pay now
                  </AppText>
                )}
              </AppStack>
            )}
          </AppFlexbox>

          <AppStack
            style={{
              gap: 0,
              alignItems: 'end',
              alignSelf: 'stretch',
              justifyContent: 'space-between',
              flexDirection: rs(['row', 'column'], mqIndex)
            }}
          >
            <AppText weight={500}>ORDER # {regCart.pkRegCart}</AppText>
            <AppText
              component={Link}
              style={{
                fontSize: 18,
                textDecoration: 'none',
                color: 'dodgerblue',
                textAlign: 'end'
              }}
              to={`/orders/${regCart.pkRegCart}`}
              weight={700}
            >
              View order details
            </AppText>
          </AppStack>
        </AppFlexbox>

        <AppStack style={{ padding: '15px 30px', gap: 16 }}>
          {regCart.cart.lineItems.map((lineItem, index) => {
            const regCartProduct = regCart.regCartProducts.find(
              (p) =>
                p.regFormSubmission.pkRegFormSubmission === lineItem.entityId &&
                p.regProduct.pkRegProduct ===
                  p.regFormSubmission.regAssociationDivisionForm.fkRegProduct &&
                lineItem.lineItemType ===
                  PAYMENT_PROVIDER_LINE_ITEM_TYPES.registration.value
            );
            let regCartMerchPackage = null;
            if (!regCartProduct) {
              regCartMerchPackage = regCart.regCartMerchPackages.find(
                (p) =>
                  p.pkRegCartMerchPackage === lineItem.entityId &&
                  lineItem.lineItemType ===
                    PAYMENT_PROVIDER_LINE_ITEM_TYPES.merch.value
              );
            }

            const productInfo = regCartProduct
              ? {
                  image: regCartProduct.regProduct.regAssociation.logoImageUrl,
                  url: `/association/${regCartProduct.regProduct.regAssociation.pkRegAssociation}`,
                  productName:
                    regCartProduct.regFormSubmission.regAssociationDivisionForm
                      .titleOverride ||
                    regCartProduct.regFormSubmission.regAssociationDivisionForm
                      .regForm.name,
                  extraDescriptions: [
                    regCartProduct.regFormSubmission.regAssociationDivisionForm
                      .regAssociationDivision.name,
                    ...(regCartProduct.regFormSubmission
                      .regAssociationDivisionOverride
                      ? [
                          {
                            label: `Moved to: ${regCartProduct.regFormSubmission.regAssociationDivisionOverride.name}`,
                            weight: 500
                          }
                        ]
                      : [])
                  ]
                }
              : regCartMerchPackage
              ? {
                  image:
                    regCartMerchPackage.merchPackage.merchPackageImages[0]
                      ?.image,
                  url: `/association/${regCartMerchPackage.regAssociation.pkRegAssociation}`,
                  productName: regCartMerchPackage.merchPackage.name,
                  extraDescriptions: regCartMerchPackage.merchPackage
                    .description
                    ? [regCartMerchPackage.merchPackage.description]
                    : []
                }
              : {
                  image: lineItem.variantImage || lineItem.previewImage,
                  productName: lineItem.productName
                };

            const storeProductData = lineItem.isBundleProduct
              ? groupedProductData.find(
                  (p) =>
                    p.ecomOrderPackage?.pkEcomOrderPackage === lineItem.entityId
                )
              : groupedProductData.find((p) => p.key === lineItem.entityId);

            return (
              <React.Fragment
                key={`${lineItem.entityId}-${lineItem.lineItemType}`}
              >
                {index > 0 && <Divider />}

                {lineItem.lineItemType ===
                PAYMENT_PROVIDER_LINE_ITEM_TYPES.ecommerce.value ? (
                  <WsAccountOrderSummaryProductsGrouped
                    baseStoreUrl={baseUrl}
                    ecomOrderPackage={storeProductData.ecomOrderPackage}
                    ecomOrderPackageLineItem={
                      storeProductData.ecomOrderPackageLineItem
                    }
                    filterListByCancelled
                    padding={0}
                    products={storeProductData.products}
                  />
                ) : (
                  <OrderListItemProductSummary
                    lineItem={lineItem}
                    {...productInfo}
                  />
                )}
              </React.Fragment>
            );
          })}

          {regCart.cart.lineItems.length > 5 && (
            <AppText
              component={Link}
              style={{
                fontSize: 20,
                textDecoration: 'none',
                color: 'dodgerblue'
              }}
              to={`/orders/${regCart.pkRegCart}`}
              weight={500}
            >
              See All {regCart.cart.lineItems.length} items
            </AppText>
          )}
        </AppStack>
      </AppStack>
    </AppCard>
  ) : (
    <AppCard
      role="listitem"
      style={{
        padding: 0,
        minWidth: rs([null, null, 600], mqIndex),
        borderRadius: rs([0, 0, 4], mqIndex)
      }}
    >
      <AppStack style={{ gap: 0 }}>
        <AppFlexbox
          style={{
            backgroundColor: '#000',
            color: '#FFF',
            opacity: 0.8,
            padding: '15px 30px',
            justifyContent: 'space-between',
            alignItems: 'start',
            gap: 10,
            flexDirection: rs(['column-reverse', 'row'], mqIndex)
          }}
        >
          <AppFlexbox
            style={{
              gap: rs([10, 40], mqIndex),
              flex: rs([1, 'unset'], mqIndex),
              alignSelf: rs(['stretch', 'unset'], mqIndex),
              justifyContent: 'space-between'
            }}
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={14} width={150} />
              <Skeleton height={16} width={150} />
            </AppStack>
            <AppStack
              style={{ gap: 10, alignItems: rs(['end', 'start'], mqIndex) }}
            >
              <Skeleton height={14} width={90} />

              <Skeleton height={16} width={120} />
            </AppStack>
          </AppFlexbox>

          <AppStack
            style={{
              gap: 10,
              alignItems: 'end',
              alignSelf: 'stretch',
              justifyContent: 'space-between',
              flexDirection: rs(['row', 'column'], mqIndex)
            }}
          >
            <Skeleton height={18} width={150} />
            <Skeleton height={20} width={200} />
          </AppStack>
        </AppFlexbox>

        <AppStack style={{ padding: '15px 30px', gap: 20 }}>
          <AppFlexbox
            style={{
              flexWrap: 'nowrap',
              flexDirection: rs(['column', 'column', 'column', 'row'], mqIndex)
            }}
          >
            <AppFlexbox
              style={{ flexWrap: 'nowrap', flex: 2, alignSelf: 'stretch' }}
            >
              <Skeleton height={100} width={100} />
              <AppStack
                style={{
                  flex: 1,
                  gap: 10,
                  textDecoration: 'none',
                  color: '#000'
                }}
              >
                <Skeleton height={12} width="50%" />
                <Skeleton height={10} width="40%" />
              </AppStack>
            </AppFlexbox>

            <AppStack
              style={{
                flex: 1,
                alignSelf: 'start',
                placeItems: 'end',
                marginLeft: 'auto',
                gap: rs([0, 30], mqIndex),
                flexDirection: rs(
                  ['row-reverse', 'row-reverse', 'row-reverse', 'column'],
                  mqIndex
                ),
                justifyContent: rs(
                  ['space-between', 'space-between', 'space-between', 'inital'],
                  mqIndex
                ),
                width: rs(['100%', '100%', '100%', 'unset'], mqIndex)
              }}
            >
              <AppStack style={{ gap: 0, alignItems: 'end' }}>
                <Skeleton height={20} width={100} />
              </AppStack>
            </AppStack>
          </AppFlexbox>
          <AppFlexbox
            style={{
              flexWrap: 'nowrap',
              flexDirection: rs(['column', 'column', 'column', 'row'], mqIndex)
            }}
          >
            <AppFlexbox
              style={{ flexWrap: 'nowrap', flex: 2, alignSelf: 'stretch' }}
            >
              <Skeleton height={100} width={100} />
              <AppStack
                style={{
                  flex: 1,
                  gap: 10,
                  textDecoration: 'none',
                  color: '#000'
                }}
              >
                <Skeleton height={12} width="50%" />
                <Skeleton height={10} width="40%" />
              </AppStack>
            </AppFlexbox>

            <AppStack
              style={{
                flex: 1,
                alignSelf: 'start',
                placeItems: 'end',
                marginLeft: 'auto',
                gap: rs([0, 30], mqIndex),
                flexDirection: rs(
                  ['row-reverse', 'row-reverse', 'row-reverse', 'column'],
                  mqIndex
                ),
                justifyContent: rs(
                  ['space-between', 'space-between', 'space-between', 'inital'],
                  mqIndex
                ),
                width: rs(['100%', '100%', '100%', 'unset'], mqIndex)
              }}
            >
              <AppStack style={{ gap: 0, alignItems: 'end' }}>
                <Skeleton height={20} width={100} />
              </AppStack>
            </AppStack>
          </AppFlexbox>
        </AppStack>
      </AppStack>
    </AppCard>
  );
};

OrderListItem.propTypes = {
  failedPaymentPlan: PropTypes.object,
  outstandingInvoice: PropTypes.object,
  processingPaymentPlan: PropTypes.object,
  regCart: PropTypes.object
};

export default OrderListItem;
